import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect, useCallback, useMemo } from 'react';
import QRCode from 'qrcode';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Plus, Copy, Download, MessageCircle, Phone, Link2, Mail,
  Globe, Radio, ShieldCheck, Loader2, ChevronRight, ChevronLeft,
  Check, Archive, Sparkles, X, Monitor, Printer, Pencil, Eye,
} from 'lucide-react';
import { useLocale } from '@/contexts/LocaleContext';

/**
 * S178 — Asistente de orígenes: formato → medio → cómo llega → campaña → campos.
 * Universal (todas las verticales). Incluye WA frase, QR llamada, webhook y correo Parseur.
 */

const ICON_MAP = {
  facebook: 'f',
  instagram: 'ig',
  tiktok: 'tt',
  linkedin: 'in',
  youtube: 'yt',
  google: 'G',
  meta: 'M',
  whatsapp: 'WA',
  send: 'Tg',
  globe: 'Web',
  mail: '@',
  search: 'SEO',
  shopping: 'ML',
  home: '🏠',
  file: '📄',
  image: '🪧',
  layout: '📺',
  book: '📰',
  credit: '💳',
  radio: '📻',
  tv: 'TV',
  calendar: '📅',
  users: '👥',
  more: '…',
};

function MiniQr({ link, size = 112 }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (!link) { setUrl(''); return; }
    QRCode.toDataURL(link, { width: size, margin: 1 }).then(setUrl).catch(() => setUrl(''));
  }, [link, size]);
  if (!url) return <div className="bg-slate-100 rounded" style={{ width: size, height: size }} />;
  return <img src={url} alt="QR" width={size} height={size} className="rounded border border-[#E2E8F0]" />;
}

function MediaTile({ m, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(m)}
      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-center transition-all ${
        selected ? 'border-[#F72585] bg-[#FFF5F9] shadow-sm' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'
      }`}
      data-testid={`media-${m.key}`}
    >
      <span className="w-9 h-9 rounded-full bg-[#F1F5F9] flex items-center justify-center text-xs font-bold text-[#0F172A]">
        {ICON_MAP[m.icon] || m.label.slice(0, 2)}
      </span>
      <span className="text-[11px] font-medium text-[#334155] leading-tight">{m.label}</span>
    </button>
  );
}

const EMPTY_FIELDS = {
  name: { enabled: true, required: true, label: 'Nombre' },
  phone: { enabled: true, required: true, label: 'Teléfono (con lada)' },
  email: { enabled: true, required: false, label: 'Correo' },
  extra1: { enabled: false, required: false, label: 'Campo adicional 1' },
  extra2: { enabled: false, required: false, label: 'Campo adicional 2' },
  extra3: { enabled: false, required: false, label: 'Campo adicional 3' },
};

const STEPS = ['formato', 'medio', 'entrada', 'detalle', 'campos', 'listo'];

export default function LeadOriginsHub() {
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState(null);
  const [items, setItems] = useState([]);
  const [freeText, setFreeText] = useState(null);
  const [wizard, setWizard] = useState(false);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [testText, setTestText] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const { t } = useLocale();
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [freshSecret, setFreshSecret] = useState(null);
  const [form, setForm] = useState({
    format: 'digital',
    medium_key: '',
    entry_mode: '',
    name: '',
    campaign_name: '',
    wa_phone: '',
    trigger_message: 'Hola, vi su anuncio y quiero información',
    twilio_phone_number: '',
    greeting: 'Hola, gracias por llamar. En un momento te atendemos.',
    context: '',
    fields: { ...EMPTY_FIELDS },
  });

  const load = useCallback(async () => {
    try {
      const [c, o] = await Promise.all([
        api.get('/tenant/lead-origins/catalog'),
        api.get('/tenant/lead-origins'),
      ]);
      setCatalog(c.data);
      setItems(o.data.items || []);
      setFreeText(o.data.free_text || null);
      if (c.data?.default_fields) {
        setForm(f => ({ ...f, fields: { ...EMPTY_FIELDS, ...c.data.default_fields } }));
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const mediaFiltered = useMemo(() => {
    const list = catalog?.media || [];
    return list.filter(m => m.format === form.format || m.format === 'both');
  }, [catalog, form.format]);

  const mediaByGroup = useMemo(() => {
    const g = {};
    mediaFiltered.forEach(m => {
      (g[m.group] = g[m.group] || []).push(m);
    });
    return g;
  }, [mediaFiltered]);

  const entryModes = useMemo(() => {
    return (catalog?.entry_modes || []).filter(e => (e.formats || []).includes(form.format));
  }, [catalog, form.format]);

  const setField = (key, patch) => {
    setForm(f => ({
      ...f,
      fields: {
        ...f.fields,
        [key]: { ...f.fields[key], ...patch },
      },
    }));
  };

  const canNext = () => {
    if (step === 0) return !!form.format;
    if (step === 1) return !!form.medium_key;
    if (step === 2) return !!form.entry_mode;
    if (step === 3) {
      if (!form.name.trim()) return false;
      if (form.entry_mode === 'whatsapp_phrase') return !!(form.wa_phone && form.trigger_message?.length >= 8);
      if (form.entry_mode === 'voice_call') return !!(form.twilio_phone_number && form.greeting);
      return true;
    }
    if (step === 4) return form.fields?.name?.enabled || form.fields?.phone?.enabled;
    return true;
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        format: form.format,
        medium_key: form.medium_key,
        entry_mode: form.entry_mode,
        campaign_name: form.campaign_name.trim() || form.name.trim(),
        fields: form.fields,
        wa_phone: form.wa_phone || undefined,
        trigger_message: form.trigger_message || undefined,
        twilio_phone_number: form.twilio_phone_number || undefined,
        greeting: form.greeting || undefined,
        context: form.context || undefined,
      };
      const r = await api.post('/tenant/lead-origins', payload);
      setCreated(r.data);
      setStep(5);
      toast.success('Origen creado');
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const next = () => {
    if (step === 4) {
      submit();
      return;
    }
    if (canNext()) setStep(s => Math.min(s + 1, STEPS.length - 1));
  };

  const back = () => setStep(s => Math.max(s - 1, 0));

  const copy = (t, msg = 'Copiado') => {
    navigator.clipboard?.writeText(t);
    toast.success(msg);
  };

  const downloadQr = async (link, name) => {
    try {
      const dataUrl = await QRCode.toDataURL(link, { width: 512, margin: 2 });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `qr-${(name || 'origen').replace(/\s+/g, '-').toLowerCase()}.png`;
      a.click();
    } catch {
      toast.error('No se pudo generar el QR');
    }
  };

  const archive = async (id) => {
    if (!window.confirm('¿Archivar este origen?')) return;
    try {
      // voice/wa use qr archive when applicable
      try {
        await api.patch(`/tenant/qr-campaigns/${id}/archive`);
      } catch {
        await api.put(`/tenant/lead-sources/${id}`, { status: 'archived' });
      }
      toast.success('Archivado');
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const runTestParse = async (originId) => {
    if (!testText.trim()) {
      toast.error('Pega un ejemplo de correo o texto del portal');
      return;
    }
    setTesting(true);
    try {
      const r = await api.post(`/tenant/lead-origins/${originId}/test-parse`, { text: testText });
      setTestResult(r.data.parsed);
      toast.success('Asistente listo — revisa los campos');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };


  const openDetail = async (id) => {
    setDetailLoading(true);
    setFreshSecret(null);
    setEditing(false);
    try {
      const r = await api.get(`/tenant/lead-origins/${id}`);
      setDetail(r.data);
      setEditForm({
        name: r.data.name || '',
        campaign_name: r.data.campaign_name || '',
        status: r.data.status || 'active',
        trigger_message: r.data.trigger_message || '',
        wa_phone: r.data.wa_phone || '',
        twilio_phone_number: r.data.twilio_phone_number || '',
        greeting: r.data.greeting || '',
        context: r.data.context || '',
        fields: r.data.fields || {},
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDetailLoading(false);
    }
  };

  const saveEdit = async () => {
    if (!detail?.id) return;
    setSaving(true);
    try {
      const r = await api.put(`/tenant/lead-origins/${detail.id}`, editForm);
      setDetail(r.data);
      setEditing(false);
      toast.success(t('origins.save'));
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const regenSecret = async () => {
    if (!detail?.id) return;
    if (!window.confirm('Regenerar secret? El anterior dejara de funcionar.')) return;
    try {
      const r = await api.post(`/tenant/lead-origins/${detail.id}/regenerate-secret`);
      setFreshSecret(r.data.webhook_secret);
      setDetail(d => d ? {
        ...d,
        webhook_url: r.data.webhook_url || d.webhook_url,
        connection: {
          ...(d.connection || {}),
          webhook_url: r.data.webhook_url,
          raw_parse_url: r.data.raw_parse_url,
        },
      } : d);
      toast.success('Secret nuevo - copialo ahora');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const ensureEmail = async () => {
    if (!detail?.id) return;
    try {
      const r = await api.post(`/tenant/lead-origins/${detail.id}/ensure-inbound-email`);
      if (r.data.webhook_secret) setFreshSecret(r.data.webhook_secret);
      await openDetail(detail.id);
      toast.success('Correo de recepcion listo');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openWizard = () => {
    setCreated(null);
    setTestResult(null);
    setStep(0);
    setWizard(true);
  };

  if (loading) return <PantallaCargando />;

  if (wizard) {
    return (
      <div className="max-w-3xl mx-auto space-y-4" data-testid="origin-wizard">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-[#0F172A]">Nuevo origen de leads</h2>
            <p className="text-xs text-[#64748B]">
              Paso {Math.min(step + 1, 5)} de 5 · Medio → cómo llega → campaña · funciona en todas las verticales
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => { setWizard(false); setStep(0); setCreated(null); }}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* progress */}
        <div className="flex gap-1">
          {STEPS.slice(0, 5).map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-[#F72585]' : 'bg-[#E2E8F0]'}`} />
          ))}
        </div>

        {/* STEP 0 format */}
        {step === 0 && (
          <div className="grid sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, format: 'digital', medium_key: '', entry_mode: '' }))}
              className={`p-5 rounded-2xl border text-left ${form.format === 'digital' ? 'border-[#F72585] bg-[#FFF5F9]' : 'border-[#E2E8F0] bg-white'}`}
              data-testid="format-digital"
            >
              <Monitor className="w-6 h-6 text-[#F72585] mb-2" />
              <p className="font-semibold text-[#0F172A]">Digital</p>
              <p className="text-xs text-[#64748B] mt-1">Ads, redes, web, marketplaces, email…</p>
            </button>
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, format: 'print', medium_key: '', entry_mode: '' }))}
              className={`p-5 rounded-2xl border text-left ${form.format === 'print' ? 'border-[#F72585] bg-[#FFF5F9]' : 'border-[#E2E8F0] bg-white'}`}
              data-testid="format-print"
            >
              <Printer className="w-6 h-6 text-[#0F172A] mb-2" />
              <p className="font-semibold text-[#0F172A]">Impreso / offline</p>
              <p className="text-xs text-[#64748B] mt-1">Carteles, flyers, radio, eventos…</p>
            </button>
          </div>
        )}

        {/* STEP 1 medium */}
        {step === 1 && (
          <div className="space-y-4">
            <p className="text-sm text-[#475569]">¿En qué medio aparece la campaña? (íconos de LATAM y Europa; elige el que uses)</p>
            {Object.entries(mediaByGroup).map(([group, list]) => (
              <div key={group}>
                <p className="text-[10px] uppercase tracking-wide text-[#94A3B8] mb-2">{group}</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                  {list.map(m => (
                    <MediaTile
                      key={m.key}
                      m={m}
                      selected={form.medium_key === m.key}
                      onSelect={(mm) => setForm(f => ({ ...f, medium_key: mm.key }))}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* STEP 2 entry */}
        {step === 2 && (
          <div className="grid sm:grid-cols-2 gap-3">
            {entryModes.map(e => {
              const Icon = e.key === 'whatsapp_phrase' ? MessageCircle
                : e.key === 'voice_call' ? Phone
                  : e.key === 'inbound_email' ? Mail
                    : Link2;
              return (
                <button
                  key={e.key}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, entry_mode: e.key }))}
                  className={`p-4 rounded-xl border text-left ${form.entry_mode === e.key ? 'border-[#F72585] bg-[#FFF5F9]' : 'border-[#E2E8F0] bg-white'}`}
                  data-testid={`entry-${e.key}`}
                >
                  <Icon className="w-5 h-5 mb-2 text-[#0F172A]" />
                  <p className="text-sm font-semibold text-[#0F172A]">{e.label}</p>
                  <p className="text-[11px] text-[#64748B] mt-1">{e.help}</p>
                </button>
              );
            })}
          </div>
        )}

        {/* STEP 3 details */}
        {step === 3 && (
          <div className="space-y-3 bg-white border border-[#E2E8F0] rounded-xl p-4">
            <div>
              <label className="text-xs font-medium text-[#334155]">Nombre del origen (para tu equipo)</label>
              <Input
                className="mt-1"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value, campaign_name: f.campaign_name || e.target.value }))}
                placeholder="Ej. Facebook Ads marzo · Cartel sucursal norte"
                data-testid="origin-name"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#334155]">Nombre de campaña (atribución en CRM)</label>
              <Input
                className="mt-1"
                value={form.campaign_name}
                onChange={e => setForm(f => ({ ...f, campaign_name: e.target.value }))}
                placeholder="Se usa en reportes y en el correo inteligente"
              />
            </div>

            {form.entry_mode === 'whatsapp_phrase' && (
              <>
                <div>
                  <label className="text-xs font-medium text-[#334155]">Número de WhatsApp (mismo en varias campañas OK)</label>
                  <Input className="mt-1" value={form.wa_phone} onChange={e => setForm(f => ({ ...f, wa_phone: e.target.value }))} placeholder="5215512345678" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#334155]">Frase de entrada (va en el anuncio / QR)</label>
                  <Input className="mt-1" value={form.trigger_message} onChange={e => setForm(f => ({ ...f, trigger_message: e.target.value }))} />
                  <p className="text-[11px] text-[#94A3B8] mt-1">Si el lead borra el texto, cae en «Mensajes libres».</p>
                </div>
              </>
            )}

            {form.entry_mode === 'voice_call' && (
              <>
                <div>
                  <label className="text-xs font-medium text-[#334155]">Número Twilio de esta campaña</label>
                  <Input className="mt-1" value={form.twilio_phone_number} onChange={e => setForm(f => ({ ...f, twilio_phone_number: e.target.value }))} placeholder="+5255..." />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#334155]">Saludo al contestar</label>
                  <Input className="mt-1" value={form.greeting} onChange={e => setForm(f => ({ ...f, greeting: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#334155]">Contexto para la IA (opcional)</label>
                  <Input className="mt-1" value={form.context} onChange={e => setForm(f => ({ ...f, context: e.target.value }))} placeholder="Campaña de carteles en Metro…" />
                </div>
              </>
            )}

            {(form.entry_mode === 'webhook' || form.entry_mode === 'inbound_email') && (
              <p className="text-xs text-[#64748B] bg-[#F8FAFC] rounded-lg p-3">
                Al terminar te damos el <strong>webhook</strong>
                {form.entry_mode === 'inbound_email' ? ' y un <strong>correo único</strong> del origen (tipo Parseur)' : ''}.
                Configura el medio o reenvía ahí los leads.
              </p>
            )}
          </div>
        )}

        {/* STEP 4 fields */}
        {step === 4 && (
          <div className="space-y-3">
            <p className="text-sm text-[#475569]">
              ¿Qué datos esperas de este origen? Por defecto nombre, teléfono y correo.
              Activa extras (color, modelo, curso…) según tu negocio — el asistente de IA los busca en el texto.
            </p>
            {Object.entries(form.fields).map(([key, f]) => (
              <div key={key} className="flex items-center gap-3 bg-white border border-[#E2E8F0] rounded-xl px-3 py-2">
                <input
                  type="checkbox"
                  checked={!!f.enabled}
                  onChange={e => setField(key, { enabled: e.target.checked })}
                  className="rounded"
                />
                <Input
                  value={f.label}
                  onChange={e => setField(key, { label: e.target.value })}
                  className="flex-1 h-8 text-sm"
                  disabled={['name', 'phone', 'email'].includes(key)}
                />
                {['name', 'phone', 'email'].includes(key) && (
                  <label className="text-[11px] text-[#64748B] flex items-center gap-1 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={!!f.required}
                      onChange={e => setField(key, { required: e.target.checked })}
                    />
                    Obligatorio
                  </label>
                )}
              </div>
            ))}
          </div>
        )}

        {/* STEP 5 done */}
        {step === 5 && created && (
          <div className="space-y-4" data-testid="origin-created">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex gap-3">
              <Check className="w-5 h-5 text-emerald-700 shrink-0" />
              <div>
                <p className="font-semibold text-emerald-900">{created.name}</p>
                <p className="text-xs text-emerald-800 mt-0.5">
                  {created.medium_label} · {created.entry_mode} · campaña «{created.campaign_name}»
                </p>
              </div>
            </div>

            {(created.wa_me_link || created.tel_link) && (
              <div className="flex gap-4 bg-white border border-[#E2E8F0] rounded-xl p-4">
                <MiniQr link={created.wa_me_link || created.tel_link} size={128} />
                <div className="space-y-2">
                  <p className="text-sm font-medium text-[#0F172A]">QR y enlace para tu pieza</p>
                  {created.trigger_message && (
                    <p className="text-xs text-[#64748B]">Frase: «{created.trigger_message}»</p>
                  )}
                  {created.twilio_phone_number && (
                    <p className="text-xs text-[#64748B]">Llamar: {created.twilio_phone_number}</p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => copy(created.wa_me_link || created.tel_link)}>
                      <Copy className="w-3.5 h-3.5 mr-1" /> Enlace
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => downloadQr(created.wa_me_link || created.tel_link, created.name)}>
                      <Download className="w-3.5 h-3.5 mr-1" /> Descargar QR
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {created.webhook_url && (
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 space-y-2">
                <p className="text-sm font-medium flex items-center gap-2"><Link2 className="w-4 h-4" /> Webhook</p>
                <code className="block text-[11px] bg-[#F1F5F9] p-2 rounded break-all font-mono">{created.webhook_url}</code>
                <Button size="sm" variant="outline" onClick={() => copy(created.webhook_url)}>Copiar URL</Button>
                {created.webhook_secret && (
                  <>
                    <p className="text-xs text-[#64748B]">Secret (solo una vez)</p>
                    <code className="block text-[11px] bg-[#F1F5F9] p-2 rounded break-all font-mono">{created.webhook_secret}</code>
                    <Button size="sm" variant="outline" onClick={() => copy(created.webhook_secret)}>Copiar secret</Button>
                  </>
                )}
              </div>
            )}

            {created.inbound_email && (
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 space-y-2">
                <p className="text-sm font-medium flex items-center gap-2"><Mail className="w-4 h-4" /> Correo inteligente (Parseur)</p>
                <code className="block text-sm bg-[#FFF5F9] p-2 rounded break-all font-mono text-[#0F172A]">{created.inbound_email}</code>
                <p className="text-[11px] text-[#64748B]">
                  Reenvía aquí los correos del portal o configúralo como destino. El asistente lee nombre, teléfono, correo y extras.
                </p>
                <Button size="sm" variant="outline" onClick={() => copy(created.inbound_email)}>Copiar correo</Button>
              </div>
            )}

            {/* test parse */}
            {(created.entry_mode === 'inbound_email' || created.entry_mode === 'webhook') && (
              <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4 space-y-2">
                <p className="text-sm font-medium flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#F72585]" /> Probar asistente</p>
                <textarea
                  className="w-full text-xs border border-[#E2E8F0] rounded-lg p-2 min-h-[100px]"
                  placeholder={"Pega un correo o texto de ejemplo:\nNombre: María López\nTel: 5512345678\nCorreo: maria@mail.com"}
                  value={testText}
                  onChange={e => setTestText(e.target.value)}
                />
                <Button size="sm" disabled={testing} onClick={() => runTestParse(created.id)} className="bg-[#F72585] text-white">
                  {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Parsear'}
                </Button>
                {testResult && (
                  <pre className="text-[11px] bg-white border border-[#E2E8F0] rounded p-2 overflow-auto">
                    {JSON.stringify(testResult, null, 2)}
                  </pre>
                )}
              </div>
            )}

            <Button onClick={() => { setWizard(false); setCreated(null); setStep(0); }} className="bg-[#0F172A] text-white">
              Volver a orígenes
            </Button>
          </div>
        )}

        {step < 5 && (
          <div className="flex justify-between pt-2">
            <Button variant="outline" size="sm" onClick={back} disabled={step === 0}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Atrás
            </Button>
            <Button
              size="sm"
              onClick={next}
              disabled={!canNext() || saving}
              className="bg-[#F72585] hover:bg-[#d91d70] text-white"
              data-testid="wizard-next"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : step === 4 ? 'Crear origen' : 'Siguiente'}
              {step < 4 && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          </div>
        )}
      </div>
    );
  }

  // ── List view ──
  return (
    <div className="space-y-6" data-testid="lead-origins-hub">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-semibold text-[#0F172A]">{t('origins.title')}</h2>
          <p className="text-sm text-[#64748B] mt-1 max-w-2xl">{t('origins.sub')}</p>
        </div>
        <Button onClick={openWizard} className="bg-[#F72585] hover:bg-[#d91d70] text-white" data-testid="new-origin">
          <Plus className="w-4 h-4 mr-1" /> {t('origins.create')}
        </Button>
      </div>

      {freeText && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 p-4 flex gap-3" data-testid="free-text-origin">
          <ShieldCheck className="w-5 h-5 text-emerald-700 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-emerald-900">{freeText.name}</p>
            <p className="text-xs text-emerald-800 mt-0.5">
              Siempre activa. Chats de WhatsApp sin frase de campaña (o si borran el texto del anuncio).
              · {freeText.lead_count || 0} leads
            </p>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="border border-dashed border-[#E2E8F0] rounded-2xl p-8 text-center">
          <Radio className="w-8 h-8 text-[#CBD5E1] mx-auto mb-2" />
          <p className="text-sm font-medium text-[#0F172A]">Aún no hay orígenes</p>
          <p className="text-xs text-[#64748B] mt-1 mb-4">Crea el primero en menos de un minuto.</p>
          <Button size="sm" onClick={openWizard} className="bg-[#F72585] text-white">Crear origen</Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {items.map(o => {
            const link = o.wa_me_link || o.tel_link;
            return (
              <div key={o.id} role="button" tabIndex={0} onClick={() => openDetail(o.id)} onKeyDown={e => { if (e.key === 'Enter') openDetail(o.id); }} className="bg-white border border-[#E2E8F0] rounded-xl p-4 flex gap-3 cursor-pointer hover:border-[#F72585]/40 text-left w-full" data-testid={`origin-card-${o.id}`}>
                {link ? <MiniQr link={link} size={88} /> : (
                  <div className="w-[88px] h-[88px] rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] flex items-center justify-center text-lg font-bold text-[#94A3B8]">
                    {(o.medium_label || '?').slice(0, 2)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#0F172A] truncate">{o.name}</p>
                  <p className="text-[11px] text-[#64748B]">
                    {o.medium_label} · {o.format === 'print' ? 'Impreso' : 'Digital'} · {labelEntry(o.entry_mode)}
                  </p>
                  {o.trigger_message && (
                    <p className="text-[11px] text-[#334155] mt-1 line-clamp-2">«{o.trigger_message}»</p>
                  )}
                  {o.inbound_email && (
                    <p className="text-[10px] font-mono text-[#64748B] truncate mt-1">{o.inbound_email}</p>
                  )}
                  <p className="text-[11px] text-[#94A3B8] mt-1">{o.lead_count || 0} leads · {o.status}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {link && (
                      <>
                        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={e => { e.stopPropagation(); copy(link); }}><Copy className="w-3 h-3" /></Button>
                        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={e => { e.stopPropagation(); downloadQr(link, o.name); }}><Download className="w-3 h-3" /></Button>
                      </>
                    )}
                    {o.inbound_email && (
                      <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={e => { e.stopPropagation(); copy(o.inbound_email); }}><Mail className="w-3 h-3" /></Button>
                    )}
                    {o.webhook_url && (
                      <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={e => { e.stopPropagation(); copy(o.webhook_url); }}><Link2 className="w-3 h-3" /></Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-7 text-[11px] text-[#94A3B8]" onClick={e => { e.stopPropagation(); archive(o.id); }}>
                      <Archive className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}


      {(detail || detailLoading) && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-3" data-testid="origin-detail-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-5">
            {detailLoading && !detail ? (
              <p className="text-sm text-[#64748B]">{t('common.loading')}</p>
            ) : detail && (
              <>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div>
                    <h3 className="text-base font-bold text-[#0F172A]">{detail.name}</h3>
                    <p className="text-xs text-[#64748B]">
                      {detail.medium_label} | {detail.entry_mode} | {detail.status} | {detail.lead_count || 0} {t('origins.leads')}
                    </p>
                  </div>
                  <button type="button" onClick={() => setDetail(null)} className="p-1 text-[#94A3B8] hover:text-[#0F172A]">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="flex gap-2 mb-4">
                  <Button size="sm" variant="outline" onClick={() => setEditing(v => !v)}>
                    <Pencil className="w-3.5 h-3.5 mr-1" /> {t('origins.edit')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setDetail(null)}>{t('origins.close')}</Button>
                </div>

                {editing && (
                  <div className="space-y-2 mb-4 border border-[#E2E8F0] rounded-xl p-3">
                    <label className="text-[11px] font-medium text-[#64748B]">Nombre</label>
                    <Input value={editForm.name || ''} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
                    <label className="text-[11px] font-medium text-[#64748B]">Campana</label>
                    <Input value={editForm.campaign_name || ''} onChange={e => setEditForm(f => ({ ...f, campaign_name: e.target.value }))} />
                    <label className="text-[11px] font-medium text-[#64748B]">Estado</label>
                    <select className="w-full border rounded-lg px-2 py-2 text-sm" value={editForm.status || 'active'}
                      onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                      <option value="active">active</option>
                      <option value="paused">paused</option>
                      <option value="archived">archived</option>
                    </select>
                    {(detail.entry_mode === 'whatsapp_phrase' || detail.trigger_message) && (
                      <>
                        <label className="text-[11px] font-medium text-[#64748B]">Frase WhatsApp</label>
                        <Input value={editForm.trigger_message || ''} onChange={e => setEditForm(f => ({ ...f, trigger_message: e.target.value }))} />
                        <label className="text-[11px] font-medium text-[#64748B]">Numero WA</label>
                        <Input value={editForm.wa_phone || ''} onChange={e => setEditForm(f => ({ ...f, wa_phone: e.target.value }))} />
                      </>
                    )}
                    {(detail.entry_mode === 'voice_call' || detail.twilio_phone_number) && (
                      <>
                        <label className="text-[11px] font-medium text-[#64748B]">Numero Twilio</label>
                        <Input value={editForm.twilio_phone_number || ''} onChange={e => setEditForm(f => ({ ...f, twilio_phone_number: e.target.value }))} />
                        <label className="text-[11px] font-medium text-[#64748B]">Saludo</label>
                        <Input value={editForm.greeting || ''} onChange={e => setEditForm(f => ({ ...f, greeting: e.target.value }))} />
                      </>
                    )}
                    <Button size="sm" className="bg-[#F72585] text-white mt-2" disabled={saving} onClick={saveEdit}>
                      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t('origins.save')}
                    </Button>
                  </div>
                )}

                <h4 className="text-sm font-semibold text-[#0F172A] mb-2 flex items-center gap-1">
                  <Eye className="w-4 h-4" /> {t('origins.connection')}
                </h4>

                {(detail.connection?.wa_me_link || detail.wa_me_link || detail.connection?.tel_link || detail.tel_link) && (
                  <div className="flex gap-3 mb-3 p-3 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
                    <MiniQr link={detail.connection?.wa_me_link || detail.wa_me_link || detail.connection?.tel_link || detail.tel_link} size={100} />
                    <div className="space-y-1 text-xs">
                      {detail.trigger_message && <p>&laquo;{detail.trigger_message}&raquo;</p>}
                      {detail.twilio_phone_number && <p>{detail.twilio_phone_number}</p>}
                      <Button size="sm" variant="outline" className="h-7" onClick={() => copy(detail.connection?.wa_me_link || detail.wa_me_link || detail.connection?.tel_link || detail.tel_link)}>
                        {t('origins.copy')}
                      </Button>
                    </div>
                  </div>
                )}

                {(detail.connection?.inbound_email || detail.inbound_email) ? (
                  <div className="mb-3 p-3 border border-[#E2E8F0] rounded-xl">
                    <p className="text-xs font-semibold text-[#334155] mb-1">{t('origins.email')}</p>
                    <code className="block text-[11px] bg-[#FFF5F9] p-2 rounded break-all font-mono">
                      {detail.connection?.inbound_email || detail.inbound_email}
                    </code>
                    <Button size="sm" variant="outline" className="mt-2 h-7" onClick={() => copy(detail.connection?.inbound_email || detail.inbound_email)}>
                      {t('origins.copy')}
                    </Button>
                  </div>
                ) : (
                  (detail.entry_mode === 'webhook' || detail.entry_mode === 'inbound_email' || detail.source_type === 'custom_webhook') && (
                    <Button size="sm" variant="outline" className="mb-3" onClick={ensureEmail}>Generar correo de recepcion</Button>
                  )
                )}

                {(detail.connection?.webhook_url || detail.webhook_url) && (
                  <div className="mb-3 p-3 border border-[#E2E8F0] rounded-xl space-y-2">
                    <p className="text-xs font-semibold text-[#334155]">{t('origins.webhook')}</p>
                    <code className="block text-[11px] bg-[#F1F5F9] p-2 rounded break-all font-mono">
                      {detail.connection?.webhook_url || detail.webhook_url}
                    </code>
                    <Button size="sm" variant="outline" className="h-7" onClick={() => copy(detail.connection?.webhook_url || detail.webhook_url)}>
                      {t('origins.copy')} URL
                    </Button>
                    {detail.connection?.raw_parse_url && (
                      <>
                        <p className="text-[11px] text-[#64748B] mt-2">Parse texto libre (IA)</p>
                        <code className="block text-[10px] bg-[#F1F5F9] p-2 rounded break-all font-mono">{detail.connection.raw_parse_url}</code>
                      </>
                    )}
                    <p className="text-[11px] text-[#64748B]">
                      {t('origins.secret')}: {detail.webhook_secret_masked || (detail.has_webhook_secret ? '****' : '-')}
                    </p>
                    {freshSecret && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                        <p className="text-[11px] font-semibold text-amber-900 mb-1">{t('origins.secretOnce')}</p>
                        <code className="block text-[11px] break-all font-mono">{freshSecret}</code>
                        <Button size="sm" variant="outline" className="mt-1 h-7" onClick={() => copy(freshSecret)}>{t('origins.copy')}</Button>
                      </div>
                    )}
                    <Button size="sm" variant="outline" className="h-7" onClick={regenSecret}>{t('origins.regen')}</Button>
                  </div>
                )}

                {(detail.connection?.how_to || []).length > 0 && (
                  <div className="text-xs text-[#64748B] space-y-1">
                    <p className="font-semibold text-[#475569]">{t('origins.howTo')}</p>
                    {(detail.connection.how_to || []).map((h, i) => (
                      <p key={i}>- {h}</p>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <div className="rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] p-4 text-xs text-[#64748B] space-y-1">
        <p className="font-semibold text-[#475569]">Cómo pensarlo</p>
        <p>1. Creas un origen por cada pieza (anuncio Facebook, cartel, portal…).</p>
        <p>2. Eliges cómo entran los leads (WhatsApp, llamada, webhook o correo).</p>
        <p>3. Defines los campos; el sistema hace de Parseur + IA para acomodarlos en esa campaña.</p>
      </div>
    </div>
  );
}

function labelEntry(mode) {
  return ({
    whatsapp_phrase: 'WhatsApp',
    voice_call: 'Llamada / QR',
    webhook: 'Webhook',
    inbound_email: 'Correo inteligente',
  })[mode] || mode || '—';
}
